import { URLS } from './urls';
import type { AdminUser, TeacherListItem } from '../utils/types';

const TOKEN_KEY = 'vav_att_token';

export const saveToken  = (t: string)  => localStorage.setItem(TOKEN_KEY, t);
export const getToken   = ()           => localStorage.getItem(TOKEN_KEY) ?? '';
export const clearToken = ()           => localStorage.removeItem(TOKEN_KEY);

export async function login(payload: { email: string; password: string }): Promise<void> {
  const res = await fetch(URLS.LOGIN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message ?? data?.detail ?? 'Login failed. Check credentials.');
  }
}

export async function verifyOtp(payload: { email: string; otp: string }): Promise<{ access_token: string; teacher_id: number | null; role: string; name?: string }> {
  const res = await fetch(URLS.VERIFY_OTP, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message ?? data?.detail ?? 'OTP verification failed.');
  saveToken(data.access_token);
  return data;
}

export async function getAdminProfile(id: string | number): Promise<AdminUser> {
  const res = await fetch(URLS.GET_ALL_TEACHERS, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail ?? data?.message ?? 'Failed to fetch profile.');

  const list: any[] = Array.isArray(data) ? data : (data.data ?? data.results ?? []);
  const t = list.find((t: any) => String(t.id) === String(id));
  if (!t) throw new Error('Teacher not found.');

  const roleMap: Record<string, AdminUser['role']> = {
    class_teacher: 'class_teacher',
    subject_teacher: 'subject_teacher',
    admin: 'admin',
    supervisor: 'supervisor',
    accountant: 'accountant',
  };

  return {
    id: String(t.id),
    name: t.full_name ?? [t.first_name, t.last_name].filter(Boolean).join(' '),
    role: roleMap[t.role] ?? 'supervisor',
    email: t.email,
    phone: t.phone,
    photoPath: t.teacher_photo,
    title: t.title,
  };
}

export async function getAllTeachers(): Promise<TeacherListItem[]> {
  const res = await fetch(URLS.GET_ALL_TEACHERS, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail ?? data?.message ?? 'Failed to fetch teachers.');

  const roleMap: Record<string, TeacherListItem['role']> = {
    class_teacher: 'class_teacher',
    subject_teacher: 'subject_teacher',
  };

  const list: any[] = Array.isArray(data) ? data : (data.data ?? data.results ?? []);
  return list.map((t: any) => ({
    id: String(t.id),
    name: t.full_name ?? [t.first_name, t.last_name].filter(Boolean).join(' '),
    role: roleMap[t.role] ?? 'subject_teacher',
    subject: t.expertise ?? t.subject ?? undefined,
    className: t.class_name ?? t.className ?? undefined,
    sectionName: t.section_name ?? t.sectionName ?? undefined,
    pin: '',
    todayStatus: null,
    markedAt: undefined,
  }));
}

export async function logout(): Promise<void> {
  try {
    await fetch(URLS.LOGOUT, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  } finally {
    clearToken();
  }
}

export async function getAttendance(params?: {
  teacher_id?: string | number;
  date?: string;
  status?: string;
}): Promise<AttendanceRecord[]> {
  const res = await fetch(URLS.GET_ATTENDANCE(params), {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message ?? data?.detail ?? 'Failed to fetch attendance.');
  return data as AttendanceRecord[];
}

export async function verifyPin(teacherId: string | number, pin: string): Promise<void> {
  const res = await fetch(URLS.VERIFY_PIN, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ teacher_id: teacherId, pin }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message ?? data?.detail ?? 'Incorrect PIN.');
  }
}

export interface AttendanceRecord {
  id: number;
  teacher_id: number;
  date: string;
  status: 'present' | 'absent' | 'late';
  note: string | null;
  photo: string | null;
  marked_at_device: string;
  marked_by: number;
  updated_by: number | null;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, b64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function markTeacherAttendance(
  teacherId: string,
  status: 'present' | 'absent' | 'late',
  photoDataUrl: string,
): Promise<AttendanceRecord> {
  const form = new FormData();
  form.append('teacher_id', teacherId);
  form.append('status', status);
  form.append('photo', dataUrlToBlob(photoDataUrl), 'photo.jpg');

  const res = await fetch(URLS.MARK_ATTENDANCE, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message ?? data?.detail ?? 'Failed to mark attendance.');
  return data.data as AttendanceRecord;
}
